import { Paper, PaperVersion, PaperAuthor, SupplementaryMaterial } from '@/types/paper';
import { Database } from '@/types/supabase';

type PaperRow = Database['public']['Tables']['papers']['Row'];
type PaperVersionRow = Database['public']['Tables']['paper_versions']['Row'];

/**
 * Maps a database paper row to a Paper object
 */
export function mapToPaper(paperRow: PaperRow, versionRows: PaperVersionRow[]): Paper {
  // Map version rows to PaperVersion[]
  const versions: PaperVersion[] = versionRows.map(v => ({
    id: v.id,
    version: v.version,
    title: v.title,
    abstract: v.abstract,
    fileUrl: v.file_url,
    externalUrl: v.external_url || undefined,
    url: v.url || undefined, // Safely access the url field
    publishedAt: v.published_at,
    changes: v.changes || [],
    // Include the research fields from the database
    research_question: v.research_question || undefined,
    approach: v.approach || undefined,
    primary_finding: v.primary_finding || undefined,
    implication: v.implication || undefined,
    supplementary_materials: (v.supplementary_materials as unknown as SupplementaryMaterial[]) || undefined
  }));
  
  // Convert authors from JSON to PaperAuthor[] if available
  let authors: PaperAuthor[] = [];
  if (paperRow.authors && typeof paperRow.authors === 'object') {
    try {
      // Attempt to parse the authors array
      if (Array.isArray(paperRow.authors)) {
        // Cast the JSON to PaperAuthor[] through unknown first to satisfy TypeScript
        authors = (paperRow.authors as unknown) as PaperAuthor[];
        
        // Ensure all authors have the isPlatformUser field set
        authors = authors.map(author => ({
          ...author,
          isPlatformUser: author.isPlatformUser === undefined ? 
            (author.id === paperRow.author_id) : author.isPlatformUser
        }));
      } else {
        console.warn('Authors data is not an array:', paperRow.authors);
      }
    } catch (error) {
      console.error('Error parsing authors data:', error);
    }
  }
  
  // If we have no authors but have an author_id, create a minimal author entry
  if (authors.length === 0 && paperRow.author_id) {
    authors.push({
      id: paperRow.author_id,
      name: 'Primary Author', // This will be replaced with user data in getPaperById
      isPlatformUser: true
    });
  }
  
  // Map the paper row to a Paper object
  return {
    id: paperRow.id,
    title: paperRow.title,
    abstract: paperRow.abstract,
    authorId: paperRow.author_id,
    authors: authors,
    category: paperRow.category,
    keywords: paperRow.keywords || [],
    currentVersion: paperRow.current_version,
    versions: versions,
    comments: [], // Comments are loaded separately
    publishedAt: paperRow.published_at,
    updatedAt: paperRow.updated_at,
    views: paperRow.views,
    licenseComplianceStatus: paperRow.license_compliance_status || 'unknown',
    reviewRequests: [], // ReviewRequests are loaded separately
    externalUrl: paperRow.external_url || undefined,
    license: paperRow.license || undefined,
    licenseFetchedAt: paperRow.license_fetched_at || undefined,
    licenseValidationError: paperRow.license_validation_error || undefined,
    visibility: (paperRow.visibility as 'public' | 'private') || 'public',
    aiReviewCount: paperRow.ai_review_count || 0,
    lastAIReviewAt: paperRow.last_ai_review_at || undefined,
    madePublicAt: paperRow.made_public_at || undefined
  };
}

/**
 * Removes duplicate authors from an array of PaperAuthor objects
 * Optimized single-pass algorithm for better performance
 */
export function deduplicateAuthors(authors: PaperAuthor[]): PaperAuthor[] {
  if (authors.length <= 1) return authors;
  
  const seen = new Set<string>();
  const result: PaperAuthor[] = [];
  
  // Single pass: track by ID or normalized name
  for (const author of authors) {
    const normalizedName = author.name?.toLowerCase().trim();
    const key = author.id || normalizedName;
    
    if (!key || seen.has(key)) continue;
    
    seen.add(key);
    result.push(author);
    
    // Also track by normalized name if we have an ID to prevent name duplicates
    if (author.id && normalizedName && !seen.has(normalizedName)) {
      seen.add(normalizedName);
    }
  }
  
  return result;
}

// Add the missing export for extractResearchFields function
export const extractResearchFields = (title: string, abstract: string): string[] => {
  // Simple keyword extraction logic - in a real implementation,
  // this would be more sophisticated using NLP techniques
  const text = `${title} ${abstract}`.toLowerCase();
  const fields: string[] = [];
  
  // Define research field keywords
  const fieldKeywords = {
    'Deep Learning': ['deep learning', 'neural network', 'cnn', 'convolutional', 'lstm', 'transformer'],
    'Machine Learning': ['machine learning', 'ml', 'algorithm', 'classification', 'regression'],
    'Artificial Intelligence': ['artificial intelligence', 'ai', 'intelligent system'],
    'Computer Vision': ['computer vision', 'image recognition', 'object detection', 'image processing'],
    'Natural Language Processing': ['natural language processing', 'nlp', 'text processing', 'language model'],
    'Data Science': ['data science', 'data analysis', 'big data', 'analytics'],
    'Robotics': ['robotics', 'robot', 'autonomous', 'automation'],
    'Cybersecurity': ['cybersecurity', 'security', 'encryption', 'privacy'],
    'COVID-19': ['covid-19', 'coronavirus', 'pandemic', 'sars-cov-2'],
    'Healthcare': ['healthcare', 'medical', 'clinical', 'diagnosis'],
    'Vaccines': ['vaccine', 'vaccination', 'immunization'],
    'Treatments': ['treatment', 'therapy', 'therapeutic'],
    'Quantum Computing': ['quantum computing', 'quantum', 'qubit'],
    'Blockchain': ['blockchain', 'cryptocurrency', 'bitcoin'],
    'IoT': ['internet of things', 'iot', 'smart device'],
    'Cloud Computing': ['cloud computing', 'aws', 'azure', 'distributed system'],
    'Bioinformatics': ['bioinformatics', 'genomics', 'proteomics', 'dna'],
    'Network Security': ['network security', 'firewall', 'intrusion detection'],
    'Software Engineering': ['software engineering', 'software development', 'agile'],
    'Database Systems': ['database', 'sql', 'nosql', 'data management']
  };
  
  // Check for each field
  for (const [field, keywords] of Object.entries(fieldKeywords)) {
    for (const keyword of keywords) {
      if (text.includes(keyword)) {
        if (!fields.includes(field)) {
          fields.push(field);
        }
        break;
      }
    }
  }
  
  return fields;
};
